import { Link } from "react-router-dom";
import { AlertOctagon, BookOpen, FileText, RotateCcw } from "lucide-react";
import { SAMPLE_TOKENS } from "../../lib/trace";

const LIMITS = [
  "Authorities, supply and creator transactions are read live from public Solana RPC nodes, which are rate-limited.",
  "Holder rankings, creator attribution, LP locks and wallet clusters come from RugCheck's indexer and may lag the chain.",
  "Prices, liquidity and trade counts come from DexScreener and cover indexed DEX pairs only.",
  "Wallet clusters and insider flags are heuristics. They are labelled as inferred and are not proof of common ownership.",
  "Results are cached for up to 60 seconds. Use Refresh to request new data.",
];

export const DataLimitations = () => (
  <section className="rounded-xl border border-trace-line bg-trace-panel p-6" data-testid="data-limitations">
    <h2 className="flex items-center gap-2 text-base font-medium text-white"><BookOpen size={16} className="text-trace-green" /> Data limitations</h2>
    <ul className="mt-4 grid gap-2 text-sm text-trace-muted md:grid-cols-2">
      {LIMITS.map((l) => <li key={l} className="flex gap-2"><span className="text-trace-green">·</span>{l}</li>)}
    </ul>
  </section>
);

export const LoadingState = () => (
  <div data-testid="scan-loading" aria-live="polite" className="space-y-4">
    <div className="relative overflow-hidden rounded-xl border border-trace-line bg-trace-panel p-6">
      <div className="scanline absolute inset-y-0 left-0 w-1/3 bg-gradient-to-r from-transparent via-trace-green/10 to-transparent" />
      <p className="font-mono text-sm text-trace-green">Tracing token…</p>
      <p className="mt-1 text-sm text-trace-muted">Querying Solana RPC, DexScreener and RugCheck. This usually takes 2–6 seconds.</p>
    </div>
    <div className="grid gap-4 lg:grid-cols-3">
      {[0, 1, 2, 3, 4, 5].map((i) => <div key={i} className={`h-40 animate-pulse rounded-xl border border-trace-line bg-trace-card/60 ${i === 0 || i === 3 ? "lg:col-span-2" : ""}`} />)}
    </div>
  </div>
);

export const ErrorState = ({ message, onRetry, retryable }) => (
  <div role="alert" data-testid="scan-error" className="rounded-xl border border-red-500/40 bg-red-500/5 p-8">
    <AlertOctagon className="text-red-400" />
    <h2 className="mt-4 text-lg font-medium text-white">We couldn't analyse this address</h2>
    <p className="mt-2 max-w-2xl text-sm text-trace-muted" data-testid="scan-error-message">{message}</p>
    <div className="mt-6 flex flex-wrap gap-3">
      {retryable && (
        <button onClick={onRetry} data-testid="scan-retry-button" className="inline-flex items-center gap-2 rounded-full border border-trace-line bg-trace-card px-4 py-2 text-sm text-white hover:border-trace-green/40">
          <RotateCcw size={14} /> Try again
        </button>
      )}
      <Link to="/scan/demo" data-testid="error-view-demo-link" className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm text-trace-muted hover:text-white">
        <FileText size={14} /> View demo report instead
      </Link>
    </div>
  </div>
);

export const EmptyState = () => (
  <div className="grid gap-6 rounded-xl border border-dashed border-trace-line p-8 md:grid-cols-2" data-testid="scan-empty">
    <div>
      <h2 className="text-lg font-medium text-white">Paste a token mint address to begin</h2>
      <p className="mt-2 text-sm text-trace-muted">
        Use the token's mint address, not a wallet or pair address. You can copy it from a block explorer or the project's official channels.
      </p>
      <Link to="/scan/demo" data-testid="empty-view-demo-link" className="mt-5 inline-flex items-center gap-2 text-sm text-trace-green hover:underline">
        <FileText size={14} /> Explore a clearly labelled demo report
      </Link>
    </div>
    <div className="space-y-2">
      <p className="eyebrow">Well-known Solana tokens</p>
      {SAMPLE_TOKENS.map((t) => (
        <Link key={t.label} to={`/scan/${t.address}`} data-testid={`empty-sample-${t.label.toLowerCase()}`}
          className="flex items-center justify-between rounded-lg border border-trace-line bg-trace-card px-4 py-2.5 text-sm transition-colors hover:border-trace-green/40">
          <span className="text-white">{t.label}</span><span className="font-mono text-xs text-trace-dim">{t.address.slice(0, 10)}…</span>
        </Link>
      ))}
    </div>
  </div>
);
